import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, PackageSearch } from "lucide-react";
import { formatIDR } from "@kanata/shared";
import { Seo } from "@/components/Seo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { checkoutApi, type ApiOrder } from "@/lib/api";

/** Order status in the customer's words. */
const statusCopy: Record<string, { label: string; detail: string }> = {
  pending_payment: {
    label: "Menunggu pembayaran",
    detail: "Pesanan sudah masuk. Kami tunggu pembayarannya, ya.",
  },
  paid: {
    label: "Sudah dibayar",
    detail: "Pembayaran diterima. Pesanan sedang kami siapkan untuk dikirim.",
  },
  payment_failed: {
    label: "Pembayaran gagal",
    detail: "Pembayarannya tidak berhasil. Kamu bisa buat kode pembayaran baru di bawah ini.",
  },
  shipped: {
    label: "Dikirim",
    detail: "Kurir sudah kami pesan untuk menjemput dan mengantar pesananmu.",
  },
  delivered: { label: "Sampai", detail: "Pesanan sudah sampai di alamat kamu." },
  completed: { label: "Selesai", detail: "Pesanan selesai. Terima kasih." },
  cancelled: { label: "Dibatalkan", detail: "Pesanan ini dibatalkan." },
};

export default function OrderLookup() {
  const [orderNumber, setOrderNumber] = useState("");
  const [phone, setPhone] = useState("");
  const [order, setOrder] = useState<ApiOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The order emails link here with ?nomor=, so the buyer only types their
  // phone. Read after mount: the page is prerendered without a query string,
  // and seeding the state from it would not match the prerendered input.
  const [params] = useSearchParams();
  const linkedNumber = params.get("nomor");
  useEffect(() => {
    if (linkedNumber) setOrderNumber(linkedNumber);
  }, [linkedNumber]);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setLoading(true);
    setError(null);
    setOrder(null);
    try {
      setOrder(await checkoutApi.lookup(orderNumber.trim(), phone.trim()));
    } catch {
      // Deliberately one message for "no such order" and "wrong phone".
      // Telling somebody the number exists but the phone is wrong is half the
      // answer, and order numbers are sequential.
      setError("Pesanan tidak ditemukan. Cek lagi nomor pesanan dan nomor HP-nya.");
    } finally {
      setLoading(false);
    }
  }

  const status = order ? (statusCopy[order.status] ?? { label: order.status, detail: "" }) : null;

  return (
    <>
      <Seo
        title="Cek pesanan — Kanata Indonesia"
        description="Cek status pesanan kamu pakai nomor pesanan dan nomor HP."
        noindex
      />

      <main className="min-h-[70vh] pt-28 sm:pt-36">
        <div className="mx-auto max-w-2xl px-5 pb-20 sm:px-8">
          <h1 className="text-3xl font-medium tracking-tight text-ink sm:text-4xl">Cek pesanan</h1>
          <p className="mt-2 text-ink/60">
            Tidak perlu akun. Masukkan nomor pesanan dan nomor HP yang kamu pakai waktu checkout.
          </p>

          <form onSubmit={onSubmit} className="mt-8 grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="mb-1.5 block">Nomor pesanan</Label>
              <Input
                value={orderNumber}
                onChange={(e) => setOrderNumber(e.target.value)}
                required
                placeholder="KNT-202608-0001"
              />
            </div>
            <div>
              <Label className="mb-1.5 block">Nomor HP</Label>
              <Input
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                inputMode="tel"
                placeholder="0815…"
              />
            </div>
            <Button type="submit" size="lg" className="sm:col-span-2" disabled={loading}>
              {loading ? <Loader2 className="animate-spin" /> : <PackageSearch />}
              Cek status
            </Button>
          </form>

          {error && (
            <p className="mt-6 rounded-xl bg-destructive/10 px-4 py-3 text-sm text-destructive">
              {error}
            </p>
          )}

          {order && status && (
            <div className="mt-10 rounded-2xl border border-ink/8 bg-cream/60 p-6">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="text-sm text-ink/50">{order.orderNumber}</p>
                  <h2 className="mt-0.5 text-xl font-medium text-ink">{status.label}</h2>
                </div>
                {order.pasangIncluded && <Badge>Dengan pemasangan</Badge>}
              </div>
              <p className="mt-2 text-sm leading-relaxed text-ink/65">{status.detail}</p>

              {/* The way back to the payment page, for a buyer who closed the
                  tab. It links to Kanata's own page rather than to
                  alturaPaymentLink — that field only ever holds a link on the
                  virtual-account path, and on the QRIS path the value behind it
                  is a QR blob, not somewhere to go.

                  payment_failed is offered too, not just pending_payment: an
                  expired QR lands there, and "buat kode baru" is exactly what
                  that buyer came here for. */}
              {order.statusToken &&
                (order.status === "pending_payment" || order.status === "payment_failed") && (
                  <Button asChild className="mt-4">
                    <Link
                      to={`/bayar/${encodeURIComponent(order.orderNumber)}?t=${encodeURIComponent(order.statusToken)}`}
                    >
                      {order.status === "payment_failed"
                        ? "Buat kode pembayaran baru"
                        : "Lanjutkan pembayaran"}
                    </Link>
                  </Button>
                )}

              {order.biteshipWaybillId && (
                <p className="mt-4 text-sm text-ink/70">
                  Nomor resi{" "}
                  <span className="font-medium text-ink">{order.biteshipWaybillId}</span>
                  {order.biteshipCourierName && <> · {order.biteshipCourierName}</>}
                </p>
              )}
              {/* Biteship's own page: where the courier says the parcel is,
                  updated as it moves, with no account needed. */}
              {order.biteshipTrackingUrl && (
                <Button asChild variant="outline" className="mt-3">
                  <a href={order.biteshipTrackingUrl} target="_blank" rel="noopener noreferrer">
                    Lacak paket
                  </a>
                </Button>
              )}

              <Separator className="my-5" />

              <ul className="space-y-2 text-sm">
                {order.items?.map((i, idx) => (
                  <li key={idx} className="flex justify-between gap-3">
                    <span className="text-ink/70">
                      {i.productName}
                      <span className="text-ink/45">
                        {" "}
                        {[i.size ? `${i.size} cm` : null, i.topColor, i.legColor && `kaki ${i.legColor}`, `×${i.qty}`]
                          .filter(Boolean)
                          .join(" · ")}
                      </span>
                    </span>
                    {i.promoGift ? (
                      // Given free: its value, struck through, so the list
                      // still adds up to the subtotal below it.
                      <span className="shrink-0 tabular-nums">
                        <del className="mr-1.5 text-xs text-ink/45">{formatIDR(i.lineTotalIdr)}</del>
                        <span className="text-brand-dark">Gratis</span>
                      </span>
                    ) : (
                      <span className="shrink-0 tabular-nums text-ink">
                        {formatIDR(i.lineTotalIdr)}
                      </span>
                    )}
                  </li>
                ))}
              </ul>

              <Separator className="my-4" />

              {(order.discountIdr ?? 0) > 0 && (
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-ink/60">Diskon{order.discountCode ? ` (${order.discountCode})` : ""}</span>
                  <span className="text-ink">−{formatIDR(order.discountIdr ?? 0)}</span>
                </div>
              )}
              {(order.packingKayuIdr ?? 0) > 0 && (
                <div className="mb-2 flex justify-between text-sm">
                  <span className="text-ink/60">Packing kayu ({order.packingKayuUnits} meja)</span>
                  {(order.promoPackingIdr ?? 0) > 0 ? (
                    <span className="tabular-nums">
                      <del className="mr-1.5 text-xs text-ink/45">{formatIDR(order.packingKayuIdr ?? 0)}</del>
                      <span className="text-brand-dark">Gratis</span>
                    </span>
                  ) : (
                    <span className="text-ink">{formatIDR(order.packingKayuIdr ?? 0)}</span>
                  )}
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-ink/60">Ongkos kirim</span>
                <span className="text-ink">{formatIDR(order.shippingFeeIdr)}</span>
              </div>
              {(order.shippingSubsidyIdr ?? 0) > 0 && (
                <div className="mt-2 flex justify-between text-sm">
                  <span className="text-ink/60">Subsidi ongkir{order.promoName ? ` ${order.promoName}` : ""}</span>
                  <span className="text-ink">−{formatIDR(order.shippingSubsidyIdr ?? 0)}</span>
                </div>
              )}
              <div className="mt-2 flex items-baseline justify-between">
                <span className="font-medium text-ink">Total</span>
                <span className="text-lg font-medium text-ink">{formatIDR(order.totalIdr)}</span>
              </div>
            </div>
          )}
        </div>
      </main>
    </>
  );
}
